import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useChartEngine from "./hooks/useChartEngine";
import useChartRecovery from "./hooks/useChartRecovery";
import useChartSettings from "./hooks/useChartSettings";
import useCandleColorsModal from "./hooks/useCandleColorsModal";
import useChartColors from "./hooks/useChartColors";
import useChartToggles from "./hooks/useChartToggles";
import usePositionLines from "./hooks/usePositionLines";
import useVisibleSpan from "./hooks/useVisibleSpan";
import useProfileTimezone from "./hooks/useProfileTimezone";
import useIndicators from "./hooks/useIndicators";
import useFavoriteIndicators from "./hooks/useFavoriteIndicators";
import usePaneLayout from "./hooks/usePaneLayout";
import useIndicatorSettingsModal from "./hooks/useIndicatorSettingsModal";
import useDrawings from "./hooks/useDrawings";
import useDrawingToolbar from "./hooks/useDrawingToolbar";
import useDrawingSettingsModal from "./hooks/useDrawingSettingsModal";
import ChartToolbar from "./components/ChartToolbar";
import ChartClock from "./components/ChartClock";
import IndicatorLegend from "./components/IndicatorLegend";
import PaneHeaders from "./components/PaneHeaders";
import CandleColorsModal from "./components/CandleColorsModal";
import IndicatorSettingsModal from "./components/IndicatorSettingsModal";
import DrawingToolbar from "./components/DrawingToolbar";
import DrawingSettingsModal from "./components/DrawingSettingsModal";
import OhlcInfo from "./components/OhlcInfo";
import ZoomMenu from "./components/ZoomMenu";
import ScrollToEndButton from "./components/ScrollToEndButton";
import TimezonePanel from "./components/TimezonePanel";
import ResizeHandle from "./components/ResizeHandle";
import { FUTURES } from "@constants";
import "./FuturesChart.sass";

/**
 * Futures chart drawn by our own engine (WebGL2 + Canvas 2D).
 * The engine starts with the settings of the user, see FuturesChart below.
 */
const LoadedFuturesChart = ({ chartSettingsApi, heightHandle }) => {
  const containerRef = useRef(null);
  const dispatch = useDispatch();

  const {
    futures: { pair },
    localization: { locale },
  } = useSelector((state) => state);

  // The time zone of the profile (E24): time axis, crosshair, clock; ⚙ saves a new one to the profile
  const profileTimezone = useProfileTimezone();
  const [isTimezonePanelOpen, setIsTimezonePanelOpen] = useState(false);
  // Stable, the panel closes on Escape through a document listener
  const closeTimezonePanel = useCallback(() => setIsTimezonePanelOpen(false), []);

  const { effectiveSettings, settings } = chartSettingsApi;

  // The position lines read the switch from the store
  useEffect(() => {
    dispatch({
      type: FUTURES.SET_POSITION_LEVELS_VISIBLE,
      payload: settings.plLinesVisible,
    });
  }, [dispatch, settings.plLinesVisible]);

  const candleColorsModal = useCandleColorsModal();

  const engine = useChartEngine({
    containerRef,
    symbol: pair.slug,
    resolution: effectiveSettings.resolution,
    chartType: effectiveSettings.chartType,
    timezone: profileTimezone.timezone,
    locale,
  });

  useChartColors(engine, chartSettingsApi, candleColorsModal.preview);

  // Bar timer and autoscroll from the chart type menu
  useChartToggles(engine, effectiveSettings);

  // After sleep or a background tab the chart reloads the missed bars
  useChartRecovery(engine);

  // Open positions of the pair: lines, pills, cards with PnL and ✕, zones
  usePositionLines(engine, pair.slug);

  // Indicators live in the engine; the list is saved in the chart settings and restored after F5
  const indicatorsApi = useIndicators(engine, chartSettingsApi);
  const favoriteIndicatorsApi = useFavoriteIndicators(chartSettingsApi);
  const priceIndicators = indicatorsApi.indicators.filter((indicator) => !indicator.isOscillator);
  // On phones the clock goes beside the toolbar only when the legend is shown under them
  const hasPriceIndicators = priceIndicators.length > 0;
  // One switch for all indicator titles: the legend on the price and every pane header
  const [areTitlesHidden, setAreTitlesHidden] = useState(false);
  const toggleTitles = () => setAreTitlesHidden((current) => !current);
  // ✏️ of an indicator opens its settings; Save and Remove go to the engine
  const indicatorSettings = useIndicatorSettingsModal(engine, indicatorsApi);
  const drawingsApi = useDrawings(engine);
  const drawingSettings = useDrawingSettingsModal(drawingsApi);
  const drawingToolbar = useDrawingToolbar(drawingsApi, chartSettingsApi, drawingSettings.open);

  // Oscillator panes under the price pane: their headers, and the bottom controls stay above them
  const paneLayout = usePaneLayout(engine);
  const panesHeight = paneLayout?.areaHeight || 0;

  // Zoom button label and the «›» button follow the visible part of the chart
  const { span, isLastBarVisible, priceAxisWidth } = useVisibleSpan(engine);

  // A span of the zoom menu only zooms, the timeframe stays
  const handleSelectSpan = (seconds) => engine?.setVisibleSpan(seconds);
  const handleScrollToEnd = () => engine?.scrollToRealtime();

  return (
    <div className="futures-chart" style={{ "--chart-panes-height": `${panesHeight}px` }}>
      <div className="futures-chart__overlay">
        <div
          className={`futures-chart__overlay-row${hasPriceIndicators ? " futures-chart__overlay-row--inline" : ""}`}
        >
          <ChartToolbar
            chartSettingsApi={chartSettingsApi}
            onOpenCandleColors={candleColorsModal.open}
            indicatorsApi={indicatorsApi}
            favoriteIndicatorsApi={favoriteIndicatorsApi}
            onEditIndicator={indicatorSettings.open}
            drawingsApi={drawingsApi}
            onEditDrawing={drawingSettings.open}
          />
          <ChartClock
            timezone={profileTimezone.timezone}
            isSettingsOpen={isTimezonePanelOpen}
            onOpenSettings={() => setIsTimezonePanelOpen(true)}
            isInline={hasPriceIndicators}
          />
        </div>
        <IndicatorLegend
          indicators={priceIndicators}
          areTitlesHidden={areTitlesHidden}
          onToggleTitles={toggleTitles}
          onSetVisible={indicatorsApi.setVisible}
          onEdit={indicatorSettings.open}
          onRemove={indicatorsApi.remove}
        />
      </div>
      <div className="futures-chart__container" ref={containerRef} />
      <ResizeHandle isDragging={heightHandle.isDragging} handleProps={heightHandle.handleProps} />
      <PaneHeaders
        paneLayout={paneLayout}
        indicators={indicatorsApi.indicators}
        areTitlesHidden={areTitlesHidden}
        onToggleTitles={toggleTitles}
        onSetVisible={indicatorsApi.setVisible}
        onEdit={indicatorSettings.open}
        onRemove={indicatorsApi.remove}
        onSetExpanded={indicatorsApi.setPaneExpanded}
      />
      <div className="futures-chart__bottom-overlay">
        <OhlcInfo engine={engine} />
      </div>
      {span && (
        <div className="futures-chart__time-axis-overlay">
          <ZoomMenu span={span} onSelectSpan={handleSelectSpan} />
        </div>
      )}
      {!isLastBarVisible && (
        <ScrollToEndButton priceAxisWidth={priceAxisWidth} onClick={handleScrollToEnd} />
      )}
      {drawingToolbar.drawing && <DrawingToolbar {...drawingToolbar} />}
      <CandleColorsModal
        isOpen={candleColorsModal.isOpen}
        onClose={candleColorsModal.close}
        onPreview={candleColorsModal.setPreview}
        chartSettingsApi={chartSettingsApi}
      />
      {indicatorSettings.indicator && (
        <IndicatorSettingsModal
          key={indicatorSettings.indicator.key}
          indicator={indicatorSettings.indicator}
          palette={indicatorSettings.palette}
          onSave={indicatorSettings.save}
          onRemove={indicatorSettings.remove}
          onClose={indicatorSettings.close}
        />
      )}
      {drawingSettings.modal && (
        <DrawingSettingsModal key={drawingSettings.modal.drawing.id} {...drawingSettings.modal} />
      )}
      {isTimezonePanelOpen && (
        <TimezonePanel
          timezone={profileTimezone.timezone}
          isSaving={profileTimezone.isSaving}
          onSelect={profileTimezone.saveTimezone}
          onClose={closeTimezonePanel}
        />
      )}
    </div>
  );
};

/**
 * Waits for the saved settings, so the chart doesn't start with the defaults
 * and jump to the saved time frame and indicators.
 *
 * @param {object} props
 * @param {object} props.heightHandle - useChartHeightFactor of the page: the handle under the time axis
 */
const FuturesChart = ({ heightHandle }) => {
  const chartSettingsApi = useChartSettings();

  if (!chartSettingsApi.isLoaded) {
    return <div className="futures-chart futures-chart--loading" />;
  }

  return <LoadedFuturesChart chartSettingsApi={chartSettingsApi} heightHandle={heightHandle} />;
};

export default FuturesChart;
